import React, { useMemo, useState } from 'react';
import { Select } from '@mantine/core';
import { FieldProps, buildSelectOptionItems, stripSelectOptionValueSuffix } from '../form-helpers';
import { SelectCaret } from './SelectCaret';

export type FormFieldSelectProps = Omit<FieldProps, 'value' | 'onChange'> & {
    value: string;
    onChange: (value: string) => void;
    availableOptions: string[];
};

function FormFieldSelect({ label, required, value, onChange, availableOptions, error, disabled, inTable }: FormFieldSelectProps) {
    const [dropdownOpened, setDropdownOpened] = useState(false);

    // `availableOptions` may contain duplicate labels (real form definitions do) — give each
    // occurrence its own unique `value` so Mantine can render them as separate options instead of
    // colliding on a duplicate key. See `buildSelectOptionItems` for why.
    const data = useMemo(() => buildSelectOptionItems(availableOptions), [availableOptions]);

    // Our own `value`/`onChange` contract is plain labels (unaffected by the above — decode/encode,
    // validation and autosave never see the suffixed keys). Resolve the current label to whichever
    // data entry has a matching label; which duplicate that happens to be doesn't matter, since
    // duplicate entries are indistinguishable once picked.
    const selectedValue = useMemo(() => data.find((item) => item.label === value)?.value ?? (value || null), [data, value]);

    return (
        <Select
            label={label}
            required={required}
            data={data}
            value={selectedValue}
            onChange={(val) => onChange(val ? stripSelectOptionValueSuffix(val) : '')}
            error={error}
            disabled={disabled}
            clearable
            mt="sm"
            maw={inTable ? '100%' : undefined}
            onDropdownOpen={() => setDropdownOpened(true)}
            onDropdownClose={() => setDropdownOpened(false)}
            rightSection={<SelectCaret dropdownOpened={dropdownOpened} showClear={!!value && !disabled} onClear={() => onChange('')} />}
        />
    );
}

export default React.memo(FormFieldSelect);
